"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var util_1 = require("@firebase/util");
var Reference_1 = require("../api/Reference");
var DataSnapshot_1 = require("../api/DataSnapshot");
var Path_1 = require("./util/Path");
var Tree_1 = require("./util/Tree");
var PriorityIndex_1 = require("./snap/indexes/PriorityIndex");
var util_2 = require("./util/util");
var ServerValues_1 = require("./util/ServerValues");
var validation_1 = require("./util/validation");
var util_3 = require("@firebase/util");
var nodeFromJSON_1 = require("./snap/nodeFromJSON");
var ChildrenNode_1 = require("./snap/ChildrenNode");
var Repo_1 = require("./Repo");
// TODO: This is pretty messy. Ideally, a lot of this would move into FirebaseData, or a transaction-specific
// component used by FirebaseData, but it has ties to user callbacks (transaction update and onComplete) as well
// as the realtime connection (to send transactions to the server). So that all needs to be decoupled first.
// For now it's part of Repo, but in its own file.
/**
 * @enum {number}
 */
var TransactionStatus;
(function (TransactionStatus) {
 // We've run the transaction and updated transactionResultData_ with the result, but it isn't currently sent to the
 // server. A transaction will go from RUN -> SENT -> RUN if it comes back from the server as rejected due to
 // mismatched hash.
 TransactionStatus[TransactionStatus["RUN"] = 0] = "RUN";
 // We've run the transaction and sent it to the server and it's currently outstanding (hasn't come back as accepted
 // or rejected yet).
 TransactionStatus[TransactionStatus["SENT"] = 1] = "SENT";
 // Temporary state used to mark completed transactions (whether successful or aborted). The transaction will be
 // removed when we get a chance to prune completed ones.
 TransactionStatus[TransactionStatus["COMPLETED"] = 2] = "COMPLETED";
 // Used when an already-sent transaction needs to be aborted (e.g. due to a conflicting set() call that was made).
 // If it comes back as unsuccessful, we'll abort it.
 TransactionStatus[TransactionStatus["SENT_NEEDS_ABORT"] = 3] = "SENT_NEEDS_ABORT";
 // Temporary state used to mark transactions that need to be aborted.
 TransactionStatus[TransactionStatus["NEEDS_ABORT"] = 4] = "NEEDS_ABORT";
})(TransactionStatus = exports.TransactionStatus || (exports.TransactionStatus = {}));
/**
 * If a transaction does not succeed after 25 retries, we abort it. Among other things this ensure that if there's
 * ever a bug causing a mismatch between client / server hashes for some data, we won't retry indefinitely.
 * @type {number}
 * @const
 * @private
 */
Repo_1.Repo.MAX_TRANSACTION_RETRIES_ = 25;
/**
 * Setup the transaction data structures
 * @private
 */
Repo_1.Repo.prototype.transactions_init_ = function () {
 /**
 * Stores queues of outstanding transactions for Firebase locations.
 *
 * @type {!Tree.>}
 * @private
 */
 this.transactionQueueTree_ = new Tree_1.Tree();
};
/**
 * Creates a new transaction, adds it to the transactions we're tracking, and sends it to the server if possible.
 *
 * @param {!Path} path Path at which to do transaction.
 * @param {function(*):*} transactionUpdate Update callback.
 * @param {?function(?Error, boolean, ?DataSnapshot)} onComplete Completion callback.
 * @param {boolean} applyLocally Whether or not to make intermediate results visible
 */
Repo_1.Repo.prototype.startTransaction = function (path, transactionUpdate, onComplete, applyLocally) {
 this.log_('transaction on ' + path);
 // Add a watch to make sure we get server updates.
 var valueCallback = function () { };
 var watchRef = new Reference_1.Reference(this, path);
 watchRef.on('value', valueCallback);
 var unwatcher = function () {
 watchRef.off('value', valueCallback);
 };
 // Initialize transaction.
 var transaction = {
 path: path,
 update: transactionUpdate,
 onComplete: onComplete,
 // One of TransactionStatus enums.
 status: null,
 // Used when combining transactions at different locations to figure out which one goes first.
 order: util_2.LUIDGenerator(),
 // Whether to raise local events for this transaction.
 applyLocally: applyLocally,
 // Count of how many times we've retried the transaction.
 retryCount: 0,
 // Function to call to clean up our .on() listener.
 unwatcher: unwatcher,
 // Stores why a transaction was aborted.
 abortReason: null,
 currentWriteId: null,
 currentInputSnapshot: null,
 currentOutputSnapshotRaw: null,
 currentOutputSnapshotResolved: null
 };
 // Run transaction initially.
 var currentState = this.getLatestState_(path);
 transaction.currentInputSnapshot = currentState;
 var newVal = transaction.update(currentState.val());
 if (newVal === undefined) {
 // Abort transaction.
 transaction.unwatcher();
 transaction.currentOutputSnapshotRaw = null;
 transaction.currentOutputSnapshotResolved = null;
 if (transaction.onComplete) {
 // We just set the input snapshot, so this cast should be safe
 var snapshot = new DataSnapshot_1.DataSnapshot(transaction.currentInputSnapshot, new Reference_1.Reference(this, transaction.path), PriorityIndex_1.PRIORITY_INDEX);
 transaction.onComplete(null, false, snapshot);
 }
 }
 else {
 validation_1.validateFirebaseData('transaction failed: Data returned ', newVal, transaction.path);
 // Mark as run and add to our queue.
 transaction.status = TransactionStatus.RUN;
 var queueNode = this.transactionQueueTree_.subTree(path);
 var nodeQueue = queueNode.getValue() || [];
 nodeQueue.push(transaction);
 queueNode.setValue(nodeQueue);
 // Update visibleData and raise events
 // Note: We intentionally raise events after updating all of our transaction state, since the user could
 // start new transactions from the event callbacks.
 var priorityForNode = void 0;
 if (typeof newVal === 'object' &&
 newVal !== null &&
 util_3.contains(newVal, '.priority')) {
 priorityForNode = util_3.safeGet(newVal, '.priority');
 util_1.assert(validation_1.isValidPriority(priorityForNode), 'Invalid priority returned by transaction. ' +
 'Priority must be a valid string, finite number, server value, or null.');
 }
 else {
 var currentNode = this.serverSyncTree_.calcCompleteEventCache(path) ||
 ChildrenNode_1.ChildrenNode.EMPTY_NODE;
 priorityForNode = currentNode.getPriority().val();
 }
 priorityForNode /** @type {null|number|string} */ = priorityForNode;
 var serverValues = this.generateServerValues();
 var newNodeUnresolved = nodeFromJSON_1.nodeFromJSON(newVal, priorityForNode);
 var newNode = ServerValues_1.resolveDeferredValueSnapshot(newNodeUnresolved, serverValues);
 transaction.currentOutputSnapshotRaw = newNodeUnresolved;
 transaction.currentOutputSnapshotResolved = newNode;
 transaction.currentWriteId = this.getNextWriteId_();
 var events = this.serverSyncTree_.applyUserOverwrite(path, newNode, transaction.currentWriteId, transaction.applyLocally);
 this.eventQueue_.raiseEventsForChangedPath(path, events);
 this.sendReadyTransactions_();
 }
};
/**
 * @param {!Path} path
 * @param {Array.=} excludeSets A specific set to exclude
 * @return {Node}
 * @private
 */
Repo_1.Repo.prototype.getLatestState_ = function (path, excludeSets) {
 return (this.serverSyncTree_.calcCompleteEventCache(path, excludeSets) ||
 ChildrenNode_1.ChildrenNode.EMPTY_NODE);
};
/**
 * Sends any already-run transactions that aren't waiting for outstanding transactions to
 * complete.
 *
 * Externally it's called with no arguments, but it calls itself recursively with a particular
 * transactionQueueTree node to recurse through the tree.
 *
 * @param {Tree.>=} node transactionQueueTree node to start at.
 * @private
 */
Repo_1.Repo.prototype.sendReadyTransactions_ = function (node) {
 var _this = this;
 if (node === void 0) { node = this.transactionQueueTree_; }
 // Before recursing, make sure any completed transactions are removed.
 if (!node) {
 this.pruneCompletedTransactionsBelowNode_(node);
 }
 if (node.getValue() !== null) {
 var queue = this.buildTransactionQueue_(node);
 util_1.assert(queue.length > 0, 'Sending zero length transaction queue');
 var allRun = queue.every(function (transaction) { return transaction.status === TransactionStatus.RUN; });
 // If they're all run (and not sent), we can send them. Else, we must wait.
 if (allRun) {
 this.sendTransactionQueue_(node.path(), queue);
 }
 }
 else if (node.hasChildren()) {
 node.forEachChild(function (childNode) {
 _this.sendReadyTransactions_(childNode);
 });
 }
};
/**
 * Given a list of run transactions, send them to the server and then handle the result (success or failure).
 *
 * @param {!Path} path The location of the queue.
 * @param {!Array.} queue Queue of transactions under the specified location.
 * @private
 */
Repo_1.Repo.prototype.sendTransactionQueue_ = function (path, queue) {
 var _this = this;
 // Mark transactions as sent and increment retry count!
 var setsToIgnore = queue.map(function (txn) {
 return txn.currentWriteId;
 });
 var latestState = this.getLatestState_(path, setsToIgnore);
 var snapToSend = latestState;
 var latestHash = latestState.hash();
 for (var i = 0; i < queue.length; i++) {
 var txn = queue[i];
 util_1.assert(txn.status === TransactionStatus.RUN, 'tryToSendTransactionQueue_: items in queue should all be run.');
 txn.status = TransactionStatus.SENT;
 txn.retryCount++;
 var relativePath = Path_1.Path.relativePath(path, txn.path);
 // If we've gotten to this point, the output snapshot must be defined.
 snapToSend = snapToSend.updateChild(relativePath /**@type {!Node} */, txn.currentOutputSnapshotRaw);
 }
 var dataToSend = snapToSend.val(true);
 var pathToSend = path;
 // Send the put.
 this.server_.put(pathToSend.toString(), dataToSend, function (status) {
 _this.log_('transaction put response', {
 path: pathToSend.toString(),
 status: status
 });
 var events = [];
 if (status === 'ok') {
 // Queue up the callbacks and fire them after cleaning up all of our transaction state, since
 // the callback could trigger more transactions or sets.
 var callbacks = [];
 for (var i = 0; i < queue.length; i++) {
 queue[i].status = TransactionStatus.COMPLETED;
 events = events.concat(_this.serverSyncTree_.ackUserWrite(queue[i].currentWriteId));
 if (queue[i].onComplete) {
 // We never unset the output snapshot, and given that this transaction is complete, it should be set
 var node = queue[i].currentOutputSnapshotResolved;
 var ref = new Reference_1.Reference(_this, queue[i].path);
 var snapshot = new DataSnapshot_1.DataSnapshot(node, ref, PriorityIndex_1.PRIORITY_INDEX);
 callbacks.push(queue[i].onComplete.bind(null, null, true, snapshot));
 }
 queue[i].unwatcher();
 }
 // Now remove the completed transactions.
 _this.pruneCompletedTransactionsBelowNode_(_this.transactionQueueTree_.subTree(path));
 // There may be pending transactions that we can now send.
 _this.sendReadyTransactions_();
 _this.eventQueue_.raiseEventsForChangedPath(path, events);
 // Finally, trigger onComplete callbacks.
 for (var i = 0; i < callbacks.length; i++) {
 util_2.exceptionGuard(callbacks[i]);
 }
 }
 else {
 // transactions are no longer sent. Update their status appropriately.
 if (status === 'datastale') {
 for (var i = 0; i < queue.length; i++) {
 if (queue[i].status === TransactionStatus.SENT_NEEDS_ABORT)
 queue[i].status = TransactionStatus.NEEDS_ABORT;
 else
 queue[i].status = TransactionStatus.RUN;
 }
 }
 else {
 util_2.warn('transaction at ' + pathToSend.toString() + ' failed: ' + status);
 for (var i = 0; i < queue.length; i++) {
 queue[i].status = TransactionStatus.NEEDS_ABORT;
 queue[i].abortReason = status;
 }
 }
 _this.rerunTransactions_(path);
 }
 }, latestHash);
};
/**
 * Finds all transactions dependent on the data at changedPath and reruns them.
 *
 * Should be called any time cached data changes.
 *
 * Return the highest path that was affected by rerunning transactions. This is the path at which events need to
 * be raised for.
 *
 * @param {!Path} changedPath The path in mergedData that changed.
 * @return {!Path} The rootmost path that was affected by rerunning transactions.
 * @private
 */
Repo_1.Repo.prototype.rerunTransactions_ = function (changedPath) {
 var rootMostTransactionNode = this.getAncestorTransactionNode_(changedPath);
 var path = rootMostTransactionNode.path();
 var queue = this.buildTransactionQueue_(rootMostTransactionNode);
 this.rerunTransactionQueue_(queue, path);
 return path;
};
/**
 * Does all the work of rerunning transactions (as well as cleans up aborted transactions and whatnot).
 *
 * @param {Array.} queue The queue of transactions to run.
 * @param {!Path} path The path the queue is for.
 * @private
 */
Repo_1.Repo.prototype.rerunTransactionQueue_ = function (queue, path) {
 if (queue.length === 0) {
 return; // Nothing to do!
 }
 // Queue up the callbacks and fire them after cleaning up all of our transaction state, since
 // the callback could trigger more transactions or sets.
 var callbacks = [];
 var events = [];
 // Ignore all of the sets we're going to re-run.
 var txnsToRerun = queue.filter(function (q) {
 return q.status === TransactionStatus.RUN;
 });
 var setsToIgnore = txnsToRerun.map(function (q) {
 return q.currentWriteId;
 });
 for (var i = 0; i < queue.length; i++) {
 var transaction = queue[i];
 var relativePath = Path_1.Path.relativePath(path, transaction.path);
 var abortTransaction = false, abortReason = void 0;
 util_1.assert(relativePath !== null, 'rerunTransactionsUnderNode_: relativePath should not be null.');
 if (transaction.status === TransactionStatus.NEEDS_ABORT) {
 abortTransaction = true;
 abortReason = transaction.abortReason;
 events = events.concat(this.serverSyncTree_.ackUserWrite(transaction.currentWriteId, true));
 }
 else if (transaction.status === TransactionStatus.RUN) {
 if (transaction.retryCount >= Repo_1.Repo.MAX_TRANSACTION_RETRIES_) {
 abortTransaction = true;
 abortReason = 'maxretry';
 events = events.concat(this.serverSyncTree_.ackUserWrite(transaction.currentWriteId, true));
 }
 else {
 // This code reruns a transaction
 var currentNode = this.getLatestState_(transaction.path, setsToIgnore);
 transaction.currentInputSnapshot = currentNode;
 var newData = queue[i].update(currentNode.val());
 if (newData !== undefined) {
 validation_1.validateFirebaseData('transaction failed: Data returned ', newData, transaction.path);
 var newDataNode = nodeFromJSON_1.nodeFromJSON(newData);
 var hasExplicitPriority = typeof newData === 'object' &&
 newData != null &&
 util_3.contains(newData, '.priority');
 if (!hasExplicitPriority) {
 // Keep the old priority if there wasn't a priority explicitly specified.
 newDataNode = newDataNode.updatePriority(currentNode.getPriority());
 }
 var oldWriteId = transaction.currentWriteId;
 var serverValues = this.generateServerValues();
 var newNodeResolved = ServerValues_1.resolveDeferredValueSnapshot(newDataNode, serverValues);
 transaction.currentOutputSnapshotRaw = newDataNode;
 transaction.currentOutputSnapshotResolved = newNodeResolved;
 transaction.currentWriteId = this.getNextWriteId_();
 // Mutates setsToIgnore in place
 setsToIgnore.splice(setsToIgnore.indexOf(oldWriteId), 1);
 events = events.concat(this.serverSyncTree_.applyUserOverwrite(transaction.path, newNodeResolved, transaction.currentWriteId, transaction.applyLocally));
 events = events.concat(this.serverSyncTree_.ackUserWrite(oldWriteId, true));
 }
 else {
 abortTransaction = true;
 abortReason = 'nodata';
 events = events.concat(this.serverSyncTree_.ackUserWrite(transaction.currentWriteId, true));
 }
 }
 }
 this.eventQueue_.raiseEventsForChangedPath(path, events);
 events = [];
 if (abortTransaction) {
 // Abort.
 queue[i].status = TransactionStatus.COMPLETED;
 // Removing a listener can trigger pruning which can muck with mergedData/visibleData (as it prunes data).
 // So defer the unwatcher until we're done.
 (function (unwatcher) {
 setTimeout(unwatcher, Math.floor(0));
 })(queue[i].unwatcher);
 if (queue[i].onComplete) {
 if (abortReason === 'nodata') {
 var ref = new Reference_1.Reference(this, queue[i].path);
 // We set this field immediately, so it's safe to cast to an actual snapshot
 var lastInput /** @type {!Node} */ = queue[i].currentInputSnapshot;
 var snapshot = new DataSnapshot_1.DataSnapshot(lastInput, ref, PriorityIndex_1.PRIORITY_INDEX);
 callbacks.push(queue[i].onComplete.bind(null, null, false, snapshot));
 }
 else {
 callbacks.push(queue[i].onComplete.bind(null, new Error(abortReason), false, null));
 }
 }
 }
 }
 // Clean up completed transactions.
 this.pruneCompletedTransactionsBelowNode_(this.transactionQueueTree_);
 // Now fire callbacks, now that we're in a good, known state.
 for (var i = 0; i < callbacks.length; i++) {
 util_2.exceptionGuard(callbacks[i]);
 }
 // Try to send the transaction result to the server.
 this.sendReadyTransactions_();
};
/**
 * Returns the rootmost ancestor node of the specified path that has a pending transaction on it, or just returns
 * the node for the given path if there are no pending transactions on any ancestor.
 *
 * @param {!Path} path The location to start at.
 * @return {!Tree.>} The rootmost node with a transaction.
 * @private
 */
Repo_1.Repo.prototype.getAncestorTransactionNode_ = function (path) {
 var front;
 // Start at the root and walk deeper into the tree towards path until we find a node with pending transactions.
 var transactionNode = this.transactionQueueTree_;
 while ((front = path.getFront()) !== null &&
 transactionNode.getValue() === null) {
 transactionNode = transactionNode.subTree(front);
 path = path.popFront();
 }
 return transactionNode;
};
/**
 * Builds the queue of all transactions at or below the specified transactionNode.
 *
 * @param {!Tree.>} transactionNode
 * @return {Array.} The generated queue.
 * @private
 */
Repo_1.Repo.prototype.buildTransactionQueue_ = function (transactionNode) {
 // Walk any child transaction queues and aggregate them into a single queue.
 var transactionQueue = [];
 this.aggregateTransactionQueuesForNode_(transactionNode, transactionQueue);
 // Sort them by the order the transactions were created.
 transactionQueue.sort(function (a, b) {
 return a.order - b.order;
 });
 return transactionQueue;
};
/**
 * @param {!Tree.>} node
 * @param {Array.} queue
 * @private
 */
Repo_1.Repo.prototype.aggregateTransactionQueuesForNode_ = function (node, queue) {
 var _this = this;
 var nodeQueue = node.getValue();
 if (nodeQueue !== null) {
 for (var i = 0; i < nodeQueue.length; i++) {
 queue.push(nodeQueue[i]);
 }
 }
 node.forEachChild(function (child) {
 _this.aggregateTransactionQueuesForNode_(child, queue);
 });
};
/**
 * Remove COMPLETED transactions at or below this node in the transactionQueueTree_.
 *
 * @param {!Tree.>} node
 * @private
 */
Repo_1.Repo.prototype.pruneCompletedTransactionsBelowNode_ = function (node) {
 var _this = this;
 var queue = node.getValue();
 if (queue) {
 var to = 0;
 for (var from = 0; from < queue.length; from++) {
 if (queue[from].status !== TransactionStatus.COMPLETED) {
 queue[to] = queue[from];
 to++;
 }
 }
 queue.length = to;
 node.setValue(queue.length > 0 ? queue : null);
 }
 node.forEachChild(function (childNode) {
 _this.pruneCompletedTransactionsBelowNode_(childNode);
 });
};
/**
 * Aborts all transactions on ancestors or descendants of the specified path. Called when doing a set() or update()
 * since we consider them incompatible with transactions.
 *
 * @param {!Path} path Path for which we want to abort related transactions.
 * @return {!Path}
 * @private
 */
Repo_1.Repo.prototype.abortTransactions_ = function (path) {
 var _this = this;
 var affectedPath = this.getAncestorTransactionNode_(path).path();
 var transactionNode = this.transactionQueueTree_.subTree(path);
 transactionNode.forEachAncestor(function (node) {
 _this.abortTransactionsOnNode_(node);
 });
 this.abortTransactionsOnNode_(transactionNode);
 transactionNode.forEachDescendant(function (node) {
 _this.abortTransactionsOnNode_(node);
 });
 return affectedPath;
};
/**
 * Abort transactions stored in this transaction queue node.
 *
 * @param {!Tree.>} node Node to abort transactions for.
 * @private
 */
Repo_1.Repo.prototype.abortTransactionsOnNode_ = function (node) {
 var queue = node.getValue();
 if (queue !== null) {
 // Queue up the callbacks and fire them after cleaning up all of our transaction state, since
 // the callback could trigger more transactions or sets.
 var callbacks = [];
 // Go through queue. Any already-sent transactions must be marked for abort, while the unsent ones
 // can be immediately aborted and removed.
 var events = [];
 var lastSent = -1;
 for (var i = 0; i < queue.length; i++) {
 if (queue[i].status === TransactionStatus.SENT_NEEDS_ABORT) {
 // Already marked. No action needed.
 }
 else if (queue[i].status === TransactionStatus.SENT) {
 util_1.assert(lastSent === i - 1, 'All SENT items should be at beginning of queue.');
 lastSent = i;
 // Mark transaction for abort when it comes back.
 queue[i].status = TransactionStatus.SENT_NEEDS_ABORT;
 queue[i].abortReason = 'set';
 }
 else {
 util_1.assert(queue[i].status === TransactionStatus.RUN, 'Unexpected transaction status in abort');
 // We can abort it immediately.
 queue[i].unwatcher();
 events = events.concat(this.serverSyncTree_.ackUserWrite(queue[i].currentWriteId, true));
 if (queue[i].onComplete) {
 var snapshot = null;
 callbacks.push(queue[i].onComplete.bind(null, new Error('set'), false, snapshot));
 }
 }
 }
 if (lastSent === -1) {
 // We're not waiting for any sent transactions. We can clear the queue.
 node.setValue(null);
 }
 else {
 // Remove the transactions we aborted.
 queue.length = lastSent + 1;
 }
 // Now fire the callbacks.
 this.eventQueue_.raiseEventsForChangedPath(node.path(), events);
 for (var i = 0; i < callbacks.length; i++) {
 util_2.exceptionGuard(callbacks[i]);
 }
 }
};

//# sourceMappingURL=Repo_transaction.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/Repo_transaction.js
// module id = 109
// module chunks = 0